import React from 'react';
import { ArrowRight } from 'lucide-react';
import { Button } from './ui/button';
import { heroData } from '../mock/landingData';
import { useTranslation } from 'react-i18next';
import { AccessibilityBadge } from './accessibility/AccessibilityBadge';

const Hero = () => {
  const { t } = useTranslation();
  
  // WhatsApp link
  const whatsappLink = `https://wa.me/5517997833002?text=${encodeURIComponent('Olá! Gostaria de falar com um especialista sobre inclusão e acessibilidade.')}`;
  
  const scrollToContact = () => {
    const element = document.getElementById('contact');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section 
      id="hero" 
      className="pt-32 pb-20 bg-gradient-to-b from-blue-50 to-white"
      aria-label={t('hero.title')}
    >
      <div className="container mx-auto px-4">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Content */}
          <div className="space-y-8">
            <div className="inline-block">
              <span 
                className="bg-teal-100 text-teal-700 px-4 py-2 rounded-full text-sm font-semibold"
                role="text"
              >
                {t('hero.badge')}
              </span>
            </div>
            
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 leading-tight">
              {t('hero.title')}
            </h1>
            
            <p className="text-lg md:text-xl text-gray-600 leading-relaxed">
              {t('hero.subtitle')}
            </p>

            <div className="flex flex-col sm:flex-row gap-4" role="group" aria-label="Ações principais">
              <Button
                onClick={scrollToContact}
                size="lg"
                className="bg-teal-600 hover:bg-teal-700 text-white text-lg px-8 py-6"
              >
                {t('hero.ctaPrimary')}
                <ArrowRight className="ml-2" size={20} aria-hidden="true" />
              </Button>
              
              <Button
                onClick={() => {
                  const element = document.getElementById('services');
                  if (element) element.scrollIntoView({ behavior: 'smooth' });
                }}
                size="lg"
                variant="outline"
                className="border-2 border-teal-600 text-teal-600 hover:bg-teal-50 text-lg px-8 py-6"
              >
                {t('hero.ctaSecondary')}
              </Button>
            </div>

            {/* Accessibility Badge */}
            <div className="pt-2">
              <AccessibilityBadge testId="hero-a11y-badge" />
            </div>

            {/* Stats */}
            <div 
              className="grid grid-cols-2 md:grid-cols-4 gap-6 pt-8"
              role="region"
              aria-label="Estatísticas de impacto"
            >
              <div className="text-center">
                <p className="text-3xl font-bold text-teal-600" aria-label="Mais de 500">500+</p>
                <p className="text-sm text-gray-600 mt-1">{t('hero.stats.events')}</p>
              </div>
              <div className="text-center">
                <p className="text-3xl font-bold text-teal-600" aria-label="Mais de 200">200+</p>
                <p className="text-sm text-gray-600 mt-1">{t('hero.stats.organizations')}</p>
              </div>
              <div className="text-center">
                <p className="text-3xl font-bold text-teal-600" aria-label="Mais de 15">15+</p>
                <p className="text-sm text-gray-600 mt-1">{t('hero.stats.experience')}</p>
              </div>
              <div className="text-center">
                <p className="text-3xl font-bold text-teal-600" aria-label="Mais de 10 mil">10k+</p>
                <p className="text-sm text-gray-600 mt-1">{t('hero.stats.people')}</p>
              </div>
            </div>
          </div>

          {/* Image */}
          <div className="relative" role="img" aria-label="Imagem ilustrativa de equipe diversa trabalhando em ambiente inclusivo">
            <div className="relative rounded-2xl overflow-hidden shadow-2xl">
              <img
                src={heroData.image}
                alt="Equipe diversa de profissionais em ambiente corporativo inclusivo, incluindo pessoas com e sem deficiência trabalhando juntas em colaboração"
                className="w-full h-[500px] object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-teal-900/30 to-transparent" aria-hidden="true"></div>
            </div>
            
            {/* Floating Card */}
            <div 
              className="absolute -bottom-6 -left-6 bg-white p-6 rounded-xl shadow-xl max-w-xs hidden md:block"
              role="complementary"
            >
              <p className="text-sm text-gray-600 mb-2">{t('hero.alignment.label')}</p>
              <p className="font-bold text-gray-900">{t('hero.alignment.ods')}</p>
              <p className="text-sm text-teal-600 mt-2">{t('hero.alignment.esg')}</p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
